import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FaCheck } from "react-icons/fa";
import { approvePaymentRequest, getPaymentRequest } from "redux/erp/paymentRequestSlice";
import { Modal, Button, FormInput } from 'components/common';
import ApprovalPrerequisites, { approvalBlockers } from './ApprovalPrerequisites';

const ApproveForm = ({ show, onHide, paymentRequest, onPrepareJournal }) => {
  const dispatch = useDispatch();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const blocked = approvalBlockers(paymentRequest).length > 0;
  const signingIncomplete = paymentRequest?.certificate_approval?.required && paymentRequest?.certificate_approval?.status !== 'verified';
  useEffect(() => { if (show) setError(''); }, [show, paymentRequest?.id]);

  const onSubmit = async () => {
    if (busy || blocked || signingIncomplete) return;
    setError('');
    setBusy(true);
    try {
      await dispatch(approvePaymentRequest({ id: paymentRequest.id, comment })).unwrap();
      await dispatch(getPaymentRequest(paymentRequest.id));
      toast.success("Payment request approved.");
      onHide();
    } catch (e) {
      setError(e?.error || e?.message || "Approval failed. Review the request and retry.");
      // The step may have moved on meanwhile (signed from the email): show where it is now.
      dispatch(getPaymentRequest(paymentRequest.id));
    }
    finally { setBusy(false); }
  };

  return (
    <Modal show={show} onHide={onHide} title={`Approve ${paymentRequest?.code}`} size="md"
      footer={<>
        <Button variant="secondary" size="sm" disabled={busy} onClick={onHide}>Cancel</Button>
        <Button variant="success" size="sm" leftIcon={FaCheck} loading={busy} aria-busy={busy} disabled={blocked || signingIncomplete} onClick={onSubmit}>{busy ? "Approving…" : "Approve"}</Button>
      </>}>
      <div className="geist-scope">
        <ApprovalPrerequisites paymentRequest={paymentRequest} onPrepareJournal={onPrepareJournal} />
        <p className="tw-text-sm tw-text-geist-fg-2 tw-mb-3">
          Confirm approval of this payment request? Your signature (from HR Hub) will be attached to the form.
        </p>
        <FormInput label="Comment (optional)" type="textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        {error && <p role="alert" className="tw-text-sm tw-text-geist-error tw-mt-3 tw-mb-0">{error}</p>}
        {paymentRequest?.certificate_approval?.required && paymentRequest?.certificate_approval?.status !== 'verified' && <p className="tw-text-sm tw-text-geist-error">Complete SmartCA signing and verification on the request first.</p>}
      </div>
    </Modal>
  );
};

ApproveForm.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  paymentRequest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    blocked_by: PropTypes.array,
    certificate_approval: PropTypes.shape({
      required: PropTypes.bool,
      status: PropTypes.string,
    }),
  }),
  onPrepareJournal: PropTypes.func,
};

export default ApproveForm;
